import { type FC, type MouseEventHandler, type ReactNode, useRef } from 'react'
import { FormattedMessage } from 'react-intl'

import { type OfferV11, type PimProductV11 } from '@redteclab/api/clients/bully'
import { type GkAirRecommendedProductDataWeb } from '@redteclab/api/clients/crosssellers-proxy-service'

import { LoadingButton } from 'base-ui'

import {
  cartIntermediateAccessorGetRedPointsRedPointsAmountWithMultiplier,
  cartIntermediateAccessorGetSellerData,
  type CartIntermediateEntryObject,
  useMultipleProductsAddToCart,
} from '../../../../cart-intermediate'
import { EXPERIMENTS_TRACK_EVENT_PDP_ADD_TO_CART_CTA } from '../../../../experiments/eventTracking'
import { usePdpBuyTogetherRecommendationTracking } from '../../../../experiments/experiment-pdp-buy-together-recommendation/usePdpBuyTogetherRecommendationTracking'
import {
  CSW_INTERACTED_KEY,
  useSetSessionStorageValuesForOptimizelyTracking,
} from '../../../../experiments/hooks'
import { recommendationAttributionRecordDirectAddToCartClicks } from '../../../../experiments/recommendation-attribution'
import {
  useOpenableControls,
  useRerenderAndExecute,
} from '../../../../generic-hooks'
import { ProductPageRxByPrincipleDialog } from '../../../../product'
import { productAccessorGetVariantCode } from '../../../model/productAccessor/productAccessorGetVariantCode'
import { useProductPageContext } from '../../productPageContext'

import { usePdpBuyTogetherUdpTracking } from './usePdpBuyTogetherUdpTracking'

type ProductRecommendationWidgetBuyTogetherAddToCartButtonProps = {
  children?: ReactNode
  className?: string
  currentOffer: OfferV11
  currentVariant: PimProductV11
  onGtmDataLayerPush: () => void
  recommendedProduct: GkAirRecommendedProductDataWeb
  rxByPrincipleDialogKeySuffix?: string
}

type GetBuyTogetherCartIntermediateProductsParams = {
  currentOffer: OfferV11
  currentOfferRedpoints: number
  currentPrescriptionType: CartIntermediateEntryObject['prescriptionType']
  product: CartIntermediateEntryObject['product']
  recommendedProducts: GkAirRecommendedProductDataWeb[]
  sellerData: CartIntermediateEntryObject['sellerData']
}

const getBuyTogetherCartIntermediateProducts = ({
  currentOffer,
  currentOfferRedpoints,
  currentPrescriptionType,
  product,
  recommendedProducts,
  sellerData,
}: GetBuyTogetherCartIntermediateProductsParams): CartIntermediateEntryObject[] => [
  {
    id: currentOffer.offerId,
    prescriptionType: currentPrescriptionType,
    product,
    redpoints: currentOfferRedpoints,
    sellerData,
  },
  ...recommendedProducts.map((recommendedProductItem) => ({
    id: recommendedProductItem.offer_id,
    prescriptionType: currentPrescriptionType,
    product: recommendedProductItem,
    redpoints: Number(recommendedProductItem.redpoints),
    sellerData,
  })),
]

const getBuyTogetherButtonName = ({
  currentVariant,
  recommendedProducts,
}: Pick<
  ProductRecommendationWidgetBuyTogetherAddToCartButtonProps,
  'currentVariant'
> & {
  recommendedProducts: GkAirRecommendedProductDataWeb[]
}): string =>
  `buy-together-${currentVariant.upid}-${recommendedProducts.map(({ UID }) => UID).join('-')}`

export const ProductRecommendationWidgetBuyTogetherAddToCartButton: FC<
  ProductRecommendationWidgetBuyTogetherAddToCartButtonProps
> = ({
  children,
  className = 'mt-7 w-full',
  currentOffer,
  currentVariant,
  onGtmDataLayerPush,
  recommendedProduct,
  rxByPrincipleDialogKeySuffix,
}) => {
  const rxByPrincipleDialogControls = useOpenableControls()
  const rerenderAndExecute = useRerenderAndExecute()

  const { currentPrescriptionType, product, rxByPrincipleControls } =
    useProductPageContext()
  const { serviceId, trackUdpAddToCart, widgetId } =
    usePdpBuyTogetherUdpTracking({
      recommendedProduct,
    })

  const submitButtonRef = useRef<HTMLButtonElement>(null)
  const currentOfferRedpoints =
    cartIntermediateAccessorGetRedPointsRedPointsAmountWithMultiplier(
      currentOffer,
    )

  const sellerData = cartIntermediateAccessorGetSellerData(currentOffer)
  const recommendedProducts = [recommendedProduct]
  const products = getBuyTogetherCartIntermediateProducts({
    currentOffer,
    currentOfferRedpoints,
    currentPrescriptionType,
    product,
    recommendedProducts,
    sellerData,
  })
  const rxByPrincipleDialogKey = rxByPrincipleDialogKeySuffix
    ? `${productAccessorGetVariantCode(product)}-${rxByPrincipleDialogKeySuffix}`
    : productAccessorGetVariantCode(product)

  const { handleAddToCart, isMutating } = useMultipleProductsAddToCart({
    currentPrescriptionType,
    products,
    rxByPrincipleSelectedReason: rxByPrincipleControls.isReasonRequired
      ? rxByPrincipleControls.selectedReason
      : undefined,
    source: 'pdp-buy-together',
  })

  const trackClick =
    useSetSessionStorageValuesForOptimizelyTracking(CSW_INTERACTED_KEY)
  const trackPdpBuyTogetherRecommendationAddToCartClick =
    usePdpBuyTogetherRecommendationTracking(
      EXPERIMENTS_TRACK_EVENT_PDP_ADD_TO_CART_CTA,
      { currentOffer, product },
    )

  const onClick: MouseEventHandler<HTMLButtonElement> = (event) => {
    if (
      rxByPrincipleControls.isReasonRequired &&
      !rxByPrincipleControls.selectedReason
    ) {
      event.preventDefault()
      rxByPrincipleDialogControls.handleOpen()

      return
    }

    recommendationAttributionRecordDirectAddToCartClicks({
      areaId: widgetId,
      products: recommendedProducts,
      serviceId,
    })
    trackUdpAddToCart()
    void handleAddToCart()
    onGtmDataLayerPush()
    trackClick()
    trackPdpBuyTogetherRecommendationAddToCartClick()
  }

  return (
    <>
      <LoadingButton
        className={className}
        data-qa-id="buy-together-with-button"
        loading={isMutating}
        name={getBuyTogetherButtonName({ currentVariant, recommendedProducts })}
        onClick={onClick}
        ref={submitButtonRef}
        slots={{ root: 'a' }}
      >
        {children ?? (
          <FormattedMessage id="buyTogetherWith.button.label.addBothToCart" />
        )}
      </LoadingButton>
      {rxByPrincipleControls.isReasonRequired ? (
        <ProductPageRxByPrincipleDialog
          autoOpenOnLocationHash={false}
          // Remount the dialog when the PDP product changes so its uncontrolled form state resets.
          key={rxByPrincipleDialogKey}
          onClose={rxByPrincipleDialogControls.handleClose}
          onOpen={rxByPrincipleDialogControls.handleOpen}
          onReasonSelect={(reason) => {
            rxByPrincipleDialogControls.handleClose()
            rxByPrincipleControls.setSelectedReason(reason)
            rerenderAndExecute(() => {
              submitButtonRef.current?.click()
            })
          }}
          open={rxByPrincipleDialogControls.isOpen}
          productName={product.name}
          reasonSet={rxByPrincipleControls.reasonSet}
        />
      ) : null}
    </>
  )
}
